<template lang="pug">
  nav.navbar.row.center.middle(:class='{ "student-navbar-fixed": inStudentResource }')
    sidebar
    .main-container.row.space-between.center.middle
      router-link(:to='isUserLoggedIn ? "/feed" : "/"')
        .row.middle(v-if='schoolGroupLogo')
          img.icon(:src='schoolGroupLogo' alt='Kimple')
          .row.middle.self-end.m-bottom-05
            h2.m-left-1.text-small.bold.blue.lowercase by
            img.small-icon.m-left-05(:src='kimpleLogo' width='60px')
        img.icon(v-else :src='kimpleLogo' alt='Kimple')
      .actions-container(v-if='!inStudentResource')
        .actions.row.end(v-if='isUserLoggedIn')
          router-link.nav-link(to='/feed' active-class='active-route' id='v-step-0' ref='firstStep')
            span.text-normal(:class='disabledText')
              | {{ $t('navbar.explore') }}
          router-link.nav-link(to='/folders' active-class='active-route' id='v-step-2')
            span.text-normal(:class='disabledText')
              | {{ $t('navbar.myFolders') }}
          router-link.nav-link(to='/programs' active-class='active-route' id='v-step-3')
            span.text-normal(:class='disabledText')
              | {{ $t('navbar.programs') }}
          router-link.nav-link(v-if='!isCustom' to='/my-stats' active-class='active-route'  id='v-step-4')
            span.text-normal(:class='disabledText')
              | {{ $t('navbar.stats') }}
          router-link.nav-link(v-if='hasKreaAccess' to='/krea' active-class='active-route')
            span.text-normal(:class='disabledText')
              | {{ $t('navbar.krea') }}
          router-link.trial-button.m4-right-4(
            v-if='isTrial'
            :to='{ name: routes.license }'
            @mouseover.native="hoverOnTrial = true"
            @mouseleave.native="hoverOnTrial = false"
            :class='{ "trial-finished": remainingDays === 0}'
          )
            | {{ remainingText }}
          language-selector.m4-right-6.row.middle.center(inNavigation)
          notification-button
          dropdown.user-menu(
            :options='userMenu'
            :showOptions='showUserMenu.show'
            right-position='-10px'
            @hideMenu='hideElement({ element: "userMenu" })'
          )
            button.width-100.row.end.middle(type='button' @click='showElement({ element: "userMenu" })')
              img.avatar(:src='userInfo.avatar || avatarDefault' alt='avatar')
              simple-svg.arrow(:filepath='dropdownArrow' :width='"16px"' :height='"8px"')
        .home-actions(v-else)
          router-link.text-normal.black.m4-right-8(v-if='!hasSubdomain' :to='{ name: routes.home }' exact-active-class='active-landing')
            | {{ $t('navbar.home') }}
          router-link.text-normal.black.m4-right-8(v-if='!hasSubdomain' :to='{ name: routes.content }' exact-active-class='active-landing')
            | {{ $t('navbar.content') }}
          router-link.text-normal.black.m4-right-8(v-if='!hasSubdomain' :to='{ name: routes.howItWorks }' exact-active-class='active-landing')
            | {{ $t('navbar.howItWorks') }}
          router-link.text-normal.black.m4-right-8(v-if='!hasSubdomain' :to='{ name: routes.impact }' exact-active-class='active-landing')
            | {{ $t('navbar.impact') }}
          language-selector.m4-right-8
          router-link.secondary-button(v-if='$route.name !== routes.login' type='button' :to='{name: routes.login}') {{ $t('navbar.login') }}
</template>

<script>
import { mapActions, mapState } from 'vuex'
import Dropdown from './Dropdown'
import LanguageSelector from './LanguageSelector'
import Sidebar from './Sidebar'
import amplitude from 'amplitude-js'
import kimpleLogo from '@/assets/ic_isologo.svg'

import avatarDefault from '@/assets/empty_profile.svg'
import dropdownArrow from '@/assets/arrow_dropdown.svg'

import { routes } from '../router'
import { logoutUser } from '../services/UserService'
import { isRole } from '../utils/users'
import { userPrimaryRoles } from '../utils/constants'
import userRole from '../mixins/userRole'
import kreaRoles from '../mixins/kreaRoles'
import NotificationButton from './Notifications/NotificationButton'

const LICENSE_NAME = {
  1: 'Freemium',
  2: 'License by program',
  3: 'Campus'
}

export default {
  components: { Dropdown, Sidebar, LanguageSelector, NotificationButton },
  mixins: [ userRole, kreaRoles ],
  data() {
    return {
      avatarDefault,
      kimpleLogo,
      dropdownArrow,
      hoverOnTrial: false,
      routes
    }
  },
  computed: {
    ...mapState({
      userInfo: state => state.userInfo.userInfo,
      userState: state => state.userInfo,
      showUserMenu: state => state.behavior.userMenu,
      showSidebar: state => state.behavior.sidebarMenu,
      hasSubdomain: state => state.whiteLabel.hasSubdomain,
      whiteLabelInfo: state => state.whiteLabel.info
    }),
    schoolGroupLogo() {
      if (this.hasSubdomain) return this.whiteLabelInfo.largeLogo
      else if (this.isUserLoggedIn && this.hasWhiteLabel) return this.userInfo.activeWhitelabelLicense.whitelabelLicenseConfiguration.largeLogo
      else return null
    },
    isUserLoggedIn() {
      return this.userState.userLoggedIn
    },
    userMenu() {
      const schoolLinkText = isRole(this.userInfo.primaryRoles, userPrimaryRoles.admin)
        ? this.$t('navbar.mySchoolGroup')
        : this.$t('navbar.mySchool')
      const profileLinks = isRole(this.userInfo.primaryRoles, userPrimaryRoles.kimple)
        ? []
        : [
          { label: this.$t('navbar.myProfile'), event: 'click', handleClick: () => this.goToRoute(routes.myProfile), shouldDisable: true },
          { label: schoolLinkText, event: 'click', handleClick: () => this.goToRoute(routes.school), shouldDisable: true },
          { label: this.$t('navbar.preferences'), event: 'click', handleClick: () => this.goToRoute(routes.preferences), shouldDisable: true }
        ]
      const trialInvitations = isRole(this.userInfo.primaryRoles, userPrimaryRoles.trial) && this.userInfo.mainTrial
        ? [{ label: this.$t('navbar.seeInvitations'),
          event: 'click',
          handleClick: () => this.showElement({ element: 'trialInviteModal' }) }]
        : []
      const changeLicense = isRole(this.userInfo.primaryRoles, userPrimaryRoles.trial) || isRole(this.userInfo.primaryRoles, userPrimaryRoles.admin)
        ? [{ label: this.$t('navbar.changeLicense'), event: 'click', handleClick: () => this.goToRoute(routes.license) }]
        : []
      return [
        ...profileLinks,
        ...trialInvitations,
        { label: this.$t('navbar.needHelp'), tag: 'a', handleClick: () => this.goToRoute(routes.faqs) },
        // { label: this.$t('navbar.needHelp'), tag: 'a', elementsToBeBinded: { target: '_blank', href: process.env.VUE_APP_FEEDBACK_FORM } },
        ...changeLicense,
        { label: this.$t('navbar.logout'), event: 'click', handleClick: () => this.logout() }
      ]
    },
    inStudentResource() {
      return this.$route.meta.isStudentPublicResource
    },
    remainingText() {
      return this.hoverOnTrial ? this.$t('navbar.wantMyPlan') : this.$t('navbar.remainingDays', { days: this.remainingDays })
    },
    disabledText() {
      return this.isTrial && this.remainingDays === 0 ? 'gray' : 'black'
    }
  },
  created() {
    if (this.isUserLoggedIn) this.logAmplify()
  },
  watch: {
    async userInfo(newValue) {
      if (newValue) {
        const { defaultNavigationLanguage } = newValue.languageConfiguration
        if (defaultNavigationLanguage) this.changeLocale(defaultNavigationLanguage)
        await this.logAmplify()
      }
    }
  },
  methods: {
    ...mapActions([
      'showElement',
      'hideElement',
      'userIsLoggedIn',
      'changeLocale',
      'getLoggedUserInfo'
    ]),
    logout() {
      logoutUser()
      this.hideElement({ element: 'userMenu' })
      if (this.$route.meta.isPrivate) this.$router.push({ name: routes.home })
    },
    async logAmplify() {
      const user = this.userInfo
      const userRole = user.primaryRoles[0].name
      const educationalLevels = userRole === 'Teacher'
        ? user.educationalLevels
        : userRole === 'Manager' ? user.schoolEducationalLevels : user.schoolGroupEducationalLevels
      var identify = new amplitude.Identify()
        .set('email', user.email)
        .set('userId', user.userId)
        .set('educationalLevels', educationalLevels)
        .set('position', user.position ? user.position.position : '')
        .set('role', user.primaryRoles[0].name)
        .set('isFirstTrial', user.mainTrial)
        .set('license', user.baseLicenses.map(lic => LICENSE_NAME[lic.code]))
        .set('schoolGroupId', user.schoolGroupId ? user.schoolGroupId : '')
        .set('schoolGroup', user.schoolGroupName ? user.schoolGroupName : '')
        .set('schoolGroupType', user.schoolGroupType ? user.schoolGroupType : '')
        .set('schoolId', user.schoolId ? user.schoolId : '')
        .set('school', user.schoolName ? user.schoolName : '')
      amplitude.getInstance().setUserId(user.userId)
      await amplitude.getInstance().identify(identify)
    },
    goToRoute(routeName) {
      this.hideElement({ element: 'userMenu' })
      this.$router.push({ name: routeName })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../scss/variables/colors';
@import '../scss/variables/sizes';
@import '../scss/variables/z-indexes';

.navbar {
  background-color: $white;
  box-shadow: 0 3px 3px 0 $shadow-gray1;
  height: $navbar-height;
  justify-content: space-between;
  padding: 10px 20px;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 5;
}

.icon {
  height: 50px;
}

.actions-container {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.options-container {
  --right-position: 20px;
}

.actions {
  max-width: 860px;
  width: 100%;
}

.home-actions {
  align-items: baseline;
  display: flex;
  justify-self: flex-end;
  align-items: center;
}

.nav-link {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-right: 32px;
}

.user-menu {
  align-items: center;
  display: flex;
  justify-content: space-between;
  position: relative;
}

.avatar {
  border-radius: 100%;
  height: 30px;
  margin-right: 4px;
  width: 30px;
}

.arrow {
  height: 14px;
  width: 14px;
}

.active-route {
  .text-normal {
    color: $blue;
    font-weight: bold;
  }
}

.active-landing {
  color: $blue;
  font-weight: 900;
}

.trial-button {
  border: 2px solid $blue;
  border-radius: 4px;
  color: $blue;
  font-weight: bold;
  font-size: 16px;
  min-width: 152px;
  padding: 8px 12px;
  text-align: center;

  &.trial-finished {
    background-color: $red;
    border-color: $red;
    color: $white;
  }

  &:hover {
    background-color: $middle-blue;
    border-color: $middle-blue;
    color: $white;
  }
}

@media screen and (max-width: $mobile-break) {
  .navbar {
    height: 65px;
    justify-content: center;
  }

  .student-navbar-fixed {
    background-color: $white;
    border-bottom: 1px solid $light-gray;
    position: fixed;
    z-index: $navbar-mobile;
  }

  .icon {
    height: 38px;
  }

  .home-actions,
  .actions-container {
    display: none;
  }
}
</style>
